/**
 * "Thumbnails Left" desktop controller.
 *
 * The main image keeps the carousel Flickity slider (swipe/animation untouched).
 * Flickity is horizontal-only, so on DESKTOP we destroy the horizontal thumbnail
 * Flickity and let CSS render the thumbnails as a static vertical column, then:
 *   - clicking a thumbnail drives the main slider (mainComp.select)
 *   - the main slider's "change" event highlights the active thumbnail
 * On MOBILE we restore the thumbnail Flickity so the layout is identical to the
 * stock "Thumbnails carousel" (bottom strip). All of this only runs for
 * <media-gallery data-thumbnails-left="true">; other layouts are untouched.
 */
(function () {
  const DESKTOP = window.matchMedia("(min-width: 768px)");
  const ITEM = ".f-product__media-thumbnails-item";

  function initGallery(gallery) {
    if (gallery.__thumbsLeftInit) return;
    gallery.__thumbsLeftInit = true;

    const mainComp = gallery.querySelector('[id^="Media-Gallery"]');
    const thumbComp = gallery.querySelector('[id^="GalleryThumbnails"]');
    if (!mainComp || !thumbComp) return;

    let mode = null;
    let changeHandler = null;

    const items = () => Array.from(thumbComp.querySelectorAll(ITEM));
    const mainInstance = () => mainComp.slider && mainComp.slider.instance;

    function whenMainReady(cb, tries = 120) {
      const inst = mainInstance();
      if (inst) return cb(inst);
      if (tries <= 0) return;
      requestAnimationFrame(() => whenMainReady(cb, tries - 1));
    }

    function setActive(index) {
      items().forEach((el, i) => {
        const active = i === index;
        el.classList.toggle("is-selected", active);
        if (active) el.scrollIntoView({ block: "nearest" });
      });
    }

    // Keep the strip static in desktop mode even if product-media.js (re)inits
    // the thumbnail Flickity asynchronously or on a breakpoint change.
    const observer = new MutationObserver(() => {
      if (mode === "desktop" && thumbComp.classList.contains("flickity-enabled")) {
        thumbComp.destroy();
      }
    });
    observer.observe(thumbComp, { attributes: true, attributeFilter: ["class"] });

    function onThumbClick(event) {
      const item = event.target.closest(ITEM);
      if (!item || !thumbComp.contains(item)) return;
      const index = Number(item.dataset.thumbnailIndex);
      if (Number.isNaN(index)) return;
      whenMainReady((inst) => inst.select(index, false, false));
    }

    function applyDesktop() {
      if (mode === "desktop") return;
      mode = "desktop";
      if (thumbComp.classList.contains("flickity-enabled")) thumbComp.destroy();
      thumbComp.addEventListener("click", onThumbClick);
      whenMainReady((inst) => {
        if (changeHandler) inst.off("change", changeHandler);
        changeHandler = (index) => setActive(index);
        inst.on("change", changeHandler);
        setActive(inst.selectedIndex || 0);
      });
    }

    function applyMobile() {
      if (mode === "mobile") return;
      mode = "mobile";
      thumbComp.removeEventListener("click", onThumbClick);
      const inst = mainInstance();
      if (inst && changeHandler) inst.off("change", changeHandler);
      changeHandler = null;
      // Let Flickity manage the active state again on mobile.
      items().forEach((el) => el.classList.remove("is-selected"));
      if (!thumbComp.classList.contains("flickity-enabled")) {
        try {
          thumbComp.initSlider();
        } catch (e) {
          /* noop */
        }
      }
    }

    const sync = () => (DESKTOP.matches ? applyDesktop() : applyMobile());
    sync();
    DESKTOP.addEventListener("change", sync);
  }

  function init() {
    document
      .querySelectorAll('media-gallery[data-thumbnails-left="true"]')
      .forEach(initGallery);
  }

  // product-media.js dispatches "product-media:loaded" (bubbling) after each gallery's
  // slider is set up — the reliable moment to take over. DOMContentLoaded can fire
  // before the gallery is ready, so also scan on that event and on window load.
  document.addEventListener("product-media:loaded", (event) => {
    const gallery =
      event.target &&
      event.target.closest &&
      event.target.closest('media-gallery[data-thumbnails-left="true"]');
    if (gallery) initGallery(gallery);
  });

  if (document.readyState !== "loading") init();
  else document.addEventListener("DOMContentLoaded", init);
  window.addEventListener("load", init);
})();
